import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Collapse, Link, Stack, Typography } from '@mui/material';
import { livyJson } from 'src/lib/livyApi';
import { ACTION_CARD, CARD_EYEBROW } from './styles';
const SEVERITY_COLOR = {
  low: 'default',
  normal: 'default',
  high: 'warning',
  urgent: 'error'
};
const SECTION_LABEL = {
  fontSize: 10.5,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
  color: 'text.secondary',
  mb: '3px'
};

/** Request ids the assistant quoted. Deduped: one card per request, however often it is named. */
export const findRequestIds = content => {
  var _content$match;
  return [...new Set((_content$match = content.match(/\breq_[0-9a-f]{8,}\b/g)) !== null && _content$match !== void 0 ? _content$match : [])];
};

/**
 * The confirmation step for a request the assistant just raised.
 *
 * The decision used to live in Livy admin, behind a tab, waiting on a second person - not because
 * anyone wanted a second approval, but because the chat had no surface that could record a yes and
 * confirming in prose looped, each confirmation raising the request again. A button can record
 * one, so the decision moves back to the person who asked for the work.
 *
 * Fetched by id rather than rendered from the reply, for the same reason as a proposal: what a
 * person confirms is the server's record of the request, not text the model produced.
 */
export function LivyRequestCard(_ref) {
  var _request$status, _request$problem, _SEVERITY_COLOR$reque, _request$heldReason;
  let requestId = _ref.requestId;
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    request = _useState2[0],
    setRequest = _useState2[1];
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    error = _useState4[0],
    setError = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    busy = _useState6[0],
    setBusy = _useState6[1];
  const _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    open = _useState8[0],
    setOpen = _useState8[1];
  const load = useCallback(async () => {
    try {
      const _await$livyJson = await livyJson("/requests/".concat(encodeURIComponent(requestId))),
        found = _await$livyJson.request;
      setRequest(found);
    } catch (e) {
      setError(e.message);
    }
  }, [requestId]);
  useEffect(() => {
    void load();
  }, [load]);
  const decide = async decision => {
    setBusy(true);
    setError(null);
    try {
      var _result$request;
      // The revision this card is showing travels with the decision. The server refuses a claim
      // whose revision no longer matches, in the same write, so an amend landing between reading
      // the card and pressing it cannot file text the reader never saw.
      const result = await livyJson('/requests', {
        method: 'POST',
        body: {
          sk: requestId,
          decision,
          expectedRevision: request === null || request === void 0 ? void 0 : request.revision
        }
      });
      setRequest(r => _objectSpread(_objectSpread({}, r !== null && r !== void 0 ? r : result.request), result.request));
      if (result.stale) {
        setError('This request changed since you opened it. Read it again, then confirm.');
        return;
      }
      // A held request states its reason in the retry notice above the button, so repeating it as
      // an error stacks two alerts saying the same thing.
      if (result.jiraError && ((_result$request = result.request) === null || _result$request === void 0 ? void 0 : _result$request.status) !== 'held') setError(result.jiraError);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  // A request that cannot be loaded is not a thing to confirm, and an id the model invented is
  // exactly what that looks like.
  if (!request) return null;
  const status = (_request$status = request.status) !== null && _request$status !== void 0 ? _request$status : 'raised';
  // Neither 'filing' nor 'held' is finished. A Jira that rejected the issue leaves a request in
  // filing; one that could not be reached at all leaves it held, and the reason for that can be a
  // config somebody then fixes. Treating either as settled leaves a confirmed request with no way
  // for anyone to try again.
  const retryable = status === 'filing' || status === 'held';
  const settled = status !== 'raised' && !retryable;
  const detail = [['Problem', (_request$problem = request.problem) !== null && _request$problem !== void 0 ? _request$problem : request.description], ['Plan', request.plan], ['Success criteria', request.successCriteria]];
  return /*#__PURE__*/React.createElement(Box, {
    sx: ACTION_CARD(settled)
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: CARD_EYEBROW
  }, settled ? 'Engineering request' : 'Engineering request, not filed'), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 14.5,
      fontWeight: 600,
      mb: '6px'
    }
  }, request.title), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "6px",
    sx: {
      flexWrap: 'wrap',
      rowGap: '6px',
      mb: '12px'
    }
  }, /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    variant: "outlined",
    label: request.type
  }), request.severity && /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    variant: "outlined",
    color: (_SEVERITY_COLOR$reque = SEVERITY_COLOR[request.severity]) !== null && _SEVERITY_COLOR$reque !== void 0 ? _SEVERITY_COLOR$reque : 'default',
    label: request.severity
  }), request.estimate && /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    variant: "outlined",
    label: "~".concat(request.estimate, " with Claude Code")
  })), detail.some(_ref2 => {
    let _ref3 = _slicedToArray(_ref2, 2),
      value = _ref3[1];
    return value;
  }) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    size: "small",
    onClick: () => setOpen(o => !o),
    sx: {
      textTransform: 'none',
      px: 0,
      minWidth: 0,
      mb: open ? '8px' : '12px'
    }
  }, open ? 'Hide details' : 'Details'), /*#__PURE__*/React.createElement(Collapse, {
    in: open,
    unmountOnExit: true
  }, /*#__PURE__*/React.createElement(Stack, {
    spacing: "10px",
    sx: {
      mb: '12px'
    }
  }, detail.map(_ref4 => {
    let _ref5 = _slicedToArray(_ref4, 2),
      label = _ref5[0],
      value = _ref5[1];
    return value ? /*#__PURE__*/React.createElement(Box, {
      key: label
    }, /*#__PURE__*/React.createElement(Typography, {
      sx: SECTION_LABEL
    }, label), /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      sx: {
        whiteSpace: 'pre-wrap'
      }
    }, value)) : null;
  })))), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    variant: "outlined",
    sx: {
      mb: '10px',
      fontSize: 12.5
    }
  }, error), settled ? /*#__PURE__*/React.createElement(Alert, {
    severity: status === 'filed' ? 'success' : 'info',
    variant: "outlined",
    sx: {
      fontSize: 12.5
    }
  }, status === 'filed' ? /*#__PURE__*/React.createElement(React.Fragment, null, "Filed as", ' ', request.jiraUrl ? /*#__PURE__*/React.createElement(Link, {
    href: request.jiraUrl,
    target: "_blank",
    rel: "noreferrer"
  }, request.jiraKey) : request.jiraKey, ".") : 'Dismissed. Nothing was filed.') : /*#__PURE__*/React.createElement(React.Fragment, null, retryable && /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    variant: "outlined",
    sx: {
      mb: '10px',
      fontSize: 12.5
    }
  }, status === 'held' ? (_request$heldReason = request.heldReason) !== null && _request$heldReason !== void 0 ? _request$heldReason : 'Jira did not take this request.' : 'Filing did not finish.', ' ', "Nothing has reached the board yet."), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "8px"
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    size: "small",
    disabled: busy,
    onClick: () => void decide('confirmed'),
    sx: {
      textTransform: 'none'
    }
  }, busy ? /*#__PURE__*/React.createElement(CircularProgress, {
    size: 16
  }) : retryable ? 'Try filing again' : 'Confirm and file'), status === 'raised' && /*#__PURE__*/React.createElement(Button, {
    size: "small",
    disabled: busy,
    onClick: () => void decide('dismissed'),
    sx: {
      textTransform: 'none'
    }
  }, "Dismiss"))));
}